/**
 * closureGauge — shared derivation of the energy-balance "closure" instrument
 * reading for the Aurora-Cockpit engineering bench (EngineeringSimCard) and the
 * EO results dock.
 *
 * The conservation closure for an EO flowsheet expresses how tightly
 *   Q_in = W_net + Q_reject
 * holds for a converged run. We surface it as a single percentage drift with
 * the project's standing tolerance bands (see CLAUDE.md):
 *
 *   GREEN  drift ≤ 0.1%   — within tolerance, run valid
 *   WARN   drift ≤ 2%     — drift, re-solve advised
 *   INVALID drift > 2%    — run should be treated as invalid
 *
 * We NEVER fabricate a closure number. When the run carries no usable closure
 * field, `tier` is `'na'` and the gauge renders "n/a" gracefully.
 *
 * EO reports `conservation.energy_rel` as a fraction, so the presentation
 * boundary converts it to percent exactly once.
 */
import type { EoSolveResult } from '../../lib/sim/eo/eoReportSection';

export type ClosureTier = 'go' | 'cond' | 'nogo' | 'na';

export interface ClosureReading {
  /** Drift as a percentage (e.g. 0.012 means 0.012%). `null` when unavailable. */
  pct: number | null;
  tier: ClosureTier;
}

const GREEN_PCT = 0.1;
const WARN_PCT = 2;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Classify a closure percentage into the standing tolerance band. */
export function closureTierFromPct(pct: number): Exclude<ClosureTier, 'na'> {
  const drift = Math.abs(pct);
  if (drift <= GREEN_PCT) return 'go';
  if (drift <= WARN_PCT) return 'cond';
  return 'nogo';
}

/**
 * Derive the closure reading from a run summary WITHOUT fabricating physics.
 * Returns `{ pct: null, tier: 'na' }` when no closure field is present.
 */
export function deriveClosure(
  run: Pick<EoSolveResult, 'conservation'> | null | undefined,
): ClosureReading {
  if (!run) return { pct: null, tier: 'na' };

  if (isFiniteNumber(run.conservation.energy_rel)) {
    const pct = run.conservation.energy_rel * 100;
    return { pct, tier: closureTierFromPct(pct) };
  }

  return { pct: null, tier: 'na' };
}

/** Fill width (0–100) for the closure micro-bar, given a reading. */
export function closureBarWidth(reading: ClosureReading): number {
  if (reading.pct === null) return 0;
  const drift = Math.abs(reading.pct);
  if (reading.tier === 'go') {
    // fraction of the 0.1% green window
    return Math.max(6, Math.min(100, (drift / GREEN_PCT) * 100));
  }
  if (reading.tier === 'cond') {
    // fraction of the way through the warn window (0.1% → 2%)
    return Math.max(20, Math.min(100, ((drift - GREEN_PCT) / (WARN_PCT - GREEN_PCT)) * 100));
  }
  return 100; // invalid pins the bar
}

/** Footer caption + matching class suffix for a closure reading. */
export function closureFoot(reading: ClosureReading): { label: string; cue: string } {
  switch (reading.tier) {
    case 'go':
      return { label: '● within 0.1%', cue: 'go' };
    case 'cond':
      return { label: '▲ drift > 0.1%', cue: 'cond' };
    case 'nogo':
      return { label: '▲ invalid > 2%', cue: 'nogo' };
    default:
      return { label: 'no run', cue: 'na' };
  }
}

/** Format a closure percentage for display (e.g. 0.012). `null` → "—". */
export function formatClosurePct(pct: number | null): string {
  if (pct === null) return '—';
  const drift = Math.abs(pct);
  // small drifts want more precision; large ones two decimals is enough
  return drift < 0.1 ? drift.toFixed(3) : drift.toFixed(2);
}
